import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Phone, Copy, AlertTriangle, ChevronLeft, PhoneOff, PhoneMissed, WifiOff, Power, ShieldAlert } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
export type Condition = 'busy' | 'unanswered' | 'unreachable' | 'off' | 'all';
const CONDITIONS = [{
  id: 'busy',
  title: 'When my phone is busy',
  shortTitle: '"When Busy"',
  icon: PhoneOff,
  code: '##67#'
}, {
  id: 'unanswered',
  title: "When I don't answer",
  shortTitle: '"When Unanswered"',
  icon: PhoneMissed,
  code: '##61#'
}, {
  id: 'unreachable',
  title: "When my phone isn't reachable",
  shortTitle: '"When Unreachable"',
  icon: WifiOff,
  code: '##62#'
}, {
  id: 'off',
  title: 'When my phone is turned off',
  shortTitle: '"When Phone Off"',
  icon: Power,
  code: '##62#'
}, {
  id: 'all',
  title: 'Answer All Incoming Calls',
  shortTitle: '"All Calls"',
  icon: Phone,
  code: '##21#'
}];
interface IosDeactivationFlowProps {
  conditionId: Condition;
  isLastActiveRule: boolean;
  onComplete: (deactivatedCondition: Condition) => void;
  onEnableAnotherRule: () => void;
  onCancelSubscription: () => void;
  onKeepSubscription: () => void;
  finishAfterDeactivation?: boolean;
  onBack: () => void;
}
export function IosDeactivationFlow({
  conditionId,
  isLastActiveRule,
  onComplete,
  onEnableAnotherRule,
  onCancelSubscription,
  onKeepSubscription,
  finishAfterDeactivation = false,
  onBack
}: IosDeactivationFlowProps) {
  const {
    isLight
  } = useTheme();
  const [step, setStep] = useState(1);
  const [verificationState, setVerificationState] = useState<'idle' | 'verifying' | 'troubleshoot'>('idle');
  const [copied, setCopied] = useState(false);
  const currentCondition = CONDITIONS.find(c => c.id === conditionId) || CONDITIONS[0];
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleDeactivationSuccess = () => {
    if (finishAfterDeactivation) {
      setStep(5);
      return;
    }
    if (isLastActiveRule) {
      setStep(3); // All rules off screen
    } else {
      onComplete(conditionId);
    }
  };
  const renderStep = () => {
    switch (step) {
      case 1:
        // Screen 1 – Confirmation
        return <motion.div key="step1" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="h-full flex flex-col p-6" data-id="element-3148" data-mp="1x2biv0y0fb4a">
            <button onClick={onBack} className={`absolute top-6 left-6 p-2 rounded-full z-10 ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-400'}`} data-id="element-3149" data-mp="1e4ifjxjh4z6d">
              <ChevronLeft className="w-5 h-5" data-id="element-3150" data-mp="18zelrmp1kl43" />
            </button>

            <div className="mt-12 mb-8 text-center flex-1 flex flex-col justify-center" data-id="element-3151" data-mp="191rcao8v83sr">
              <div className={`w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center ${isLight ? 'bg-red-50 text-red-600' : 'bg-red-500/10 text-red-400'}`} data-id="element-3152" data-mp="1dzj2ndtv3huu">
                <currentCondition.icon className="w-10 h-10" data-id="element-3153" data-mp="l7zfjiu59jnk" />
              </div>
              <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3154" data-mp="q9orr1t3s3d8">
                Turn Off AI for {currentCondition.shortTitle}?
              </h1>
              <p className={`text-sm leading-relaxed max-w-xs mx-auto ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3155" data-mp="100b5srl68ltt">
                Your AI will no longer answer your calls when your phone is{' '}
                {currentCondition.id === 'busy' ? 'busy' : currentCondition.id === 'unanswered' ? 'unanswered' : currentCondition.id === 'unreachable' ? 'unreachable' : currentCondition.id === 'off' ? 'turned off' : 'ringing'}
                . You can turn this back on at any time.
              </p>
            </div>

            <div className="mt-auto space-y-3" data-id="element-3156" data-mp="2nwrbh3ov20s7">
              <Button fullWidth onClick={() => setStep(6)} data-id="element-3157" data-mp="2nvzdxnbpdsza">
                Continue
              </Button>
              <button onClick={onBack} className={`w-full py-3 text-sm font-medium transition-colors ${isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-400 hover:text-zinc-200'}`} data-id="element-3158" data-mp="31w0et2ryx0er">
                Cancel
              </button>
            </div>
          </motion.div>;
      case 2:
        // Screen 2 – Carrier Deactivation & Verification
        return <motion.div key="step2" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="h-full flex flex-col p-6 overflow-y-auto pb-24" data-id="element-3159" data-mp="3e8uowvtgc76p">
            <button onClick={() => {
            if (verificationState === 'idle') setStep(6);else setVerificationState('idle');
          }} className={`absolute top-6 left-6 p-2 rounded-full z-10 ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-400'}`} data-id="element-3160" data-mp="2vep847ly1he0">
              <ChevronLeft className="w-5 h-5" data-id="element-3161" data-mp="jqprbj9mnss2" />
            </button>

            <div className="mt-12 mb-8 text-center" data-id="element-3162" data-mp="jrivcch7c46i">
              <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3163" data-mp="oq2hodzpjhze">
                Updating Your Phone
              </h1>
              <div className="flex items-center gap-2 max-w-[120px] mx-auto" data-id="element-3164" data-mp="i90z10v63w1p">
                <div className="h-1.5 flex-1 rounded-full bg-vexu" data-id="element-3165" data-mp="dahaqtt3q866" />
              </div>
            </div>

            <AnimatePresence mode="wait" data-id="element-3166" data-mp="995h8d73x2sp">
              {verificationState === 'idle' && <motion.div key="idle" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="space-y-6" data-id="element-3167" data-mp="15z4z4y95ddny">
                  <div className={`p-5 rounded-3xl ${isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-white/5 border border-white/10'}`} data-id="element-3168" data-mp="1oqo0l0niwjnv">
                    <h2 className={`text-lg font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3169" data-mp="32mfp7s0tke4q">
                      {currentCondition.title}
                    </h2>
                    <p className={`text-sm mb-6 ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3170" data-mp="3bkso9cfnxplc">
                      We'll remove your carrier's call forwarding for this
                      situation so your AI assistant will no longer answer these
                      calls.
                    </p>

                    <div className="mb-3" data-id="element-3171" data-mp="3val96ck1tz3i">
                      <label className={`text-xs font-bold uppercase tracking-wider mb-1.5 block ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-3172" data-mp="3vbmnqr09x3pz">
                        Carrier Deactivation Code (USSD)
                      </label>
                      <div className={`flex items-center justify-between p-3 rounded-xl border ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-black/20 border-white/10'}`} data-id="element-3173" data-mp="d5rqy6jtze4k">
                        <code className={`text-base font-mono font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3174" data-mp="3f7cdmvu5ltxi">
                          {currentCondition.code}
                        </code>
                        <button onClick={() => handleCopy(currentCondition.code)} className={`p-2 rounded-lg transition-colors ${copied ? 'bg-vexu/10 text-vexu' : isLight ? 'bg-white border border-slate-200 text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-3175" data-mp="31g7leet483x7">
                          {copied ? <CheckCircle2 className="w-4 h-4" data-id="element-3176" data-mp="35g8102ryal5u" /> : <Copy className="w-4 h-4" data-id="element-3177" data-mp="2hq3xdverileu" />}
                        </button>
                      </div>
                    </div>
                    <p className={`text-xs leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-3178" data-mp="23m6f6hh5kna3">
                      This code tells your mobile carrier to stop forwarding
                      calls when your phone is{' '}
                      {currentCondition.id === 'busy' ? 'busy' : currentCondition.id === 'unanswered' ? 'unanswered' : currentCondition.id === 'unreachable' ? 'unreachable' : currentCondition.id === 'off' ? 'turned off' : 'ringing'}
                      .
                    </p>
                  </div>

                  <div data-id="element-3179" data-mp="10yjn78v78cc6">
                    <Button fullWidth onClick={() => {
                  window.open('tel:' + encodeURIComponent(currentCondition.code), '_self');
                  setVerificationState('verifying');
                }} className="flex items-center justify-center gap-2" data-id="element-3180" data-mp="1127ee1d68mnq">
                      <Phone className="w-4 h-4" data-id="element-3181" data-mp="2rh2tx3lcy00m" />
                      Dial Deactivation Code
                    </Button>
                    <p className={`text-xs leading-relaxed mt-3 text-center ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-3182" data-mp="2dgs4m4zp4thk">
                      Your Phone app will open with the deactivation code ready
                      to dial. Return to Vexu after completing the request.
                    </p>
                  </div>
                </motion.div>}

              {verificationState === 'verifying' && <motion.div key="verifying" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="space-y-6" data-id="element-3183" data-mp="6qu8g60gfc97">
                  <div className="text-center" data-id="element-3184" data-mp="1w6xzcw2wi4da">
                    <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-3185" data-mp="29znsdkguk9jw">
                      <CheckCircle2 className="w-8 h-8" data-id="element-3186" data-mp="2jxafuvc7p9fh" />
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3187" data-mp="2f0jym4oo87sh">
                      Confirm Carrier Deactivation
                    </h2>
                    <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3188" data-mp="kn2aafgfz6we">
                      Check the message displayed by your carrier after dialing
                      the deactivation code. Continue only if you see a
                      successful confirmation.
                    </p>
                  </div>

                  <div className="space-y-2.5" data-id="element-3189" data-mp="2yp2zu89jkxvg">
                    {['Setting Erasure Succeeded', 'Call Forwarding Disabled'].map((msg, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.1 + i * 0.1
                }} className={`rounded-2xl px-4 py-3 text-center ${isLight ? 'bg-slate-100 border border-slate-200' : 'bg-white/5 border border-white/10'}`} data-id="element-3190" data-mp="38jl71ne63bk0" data-mpi={i}>
                        <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-3191" data-mp="3rhodztkwt7qo">
                          Example carrier message
                        </p>
                        <p className={`text-sm font-medium whitespace-pre-line ${isLight ? 'text-slate-800' : 'text-zinc-200'}`} data-id="element-3192" data-mp="o328dmo0pbki">
                          {msg}
                        </p>
                      </motion.div>)}
                  </div>

                  <div className={`p-5 rounded-3xl ${isLight ? 'bg-vexu/5 border border-vexu/20' : 'bg-vexu/10 border border-vexu/20'}`} data-id="element-3193" data-mp="16rgz08ufanks">
                    <h3 className={`text-sm font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3194" data-mp="11z5yb3ruyxr6">
                      You're ready to continue if you see:
                    </h3>
                    <ul className="space-y-2.5" data-id="element-3195" data-mp="dit1rkzsas96">
                      {['Deactivation Successful', 'Call Forwarding Disabled', 'Service Removed', 'Registration Cancelled'].map(item => <li key={item} className="flex items-start gap-2.5 text-sm" data-id="element-3196" data-mp="1a7ifr08aj0pv">
                          <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-3197" data-mp="10iqsv6i376lt" />
                          <span className={`leading-relaxed ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-3198" data-mp="1o68h4s257mk1">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                  </div>

                  <div className="space-y-3 pt-1" data-id="element-3199" data-mp="3nft5cw3xqbwj">
                    <Button fullWidth onClick={handleDeactivationSuccess} data-id="element-3200" data-mp="3nei1kwahp45f">
                      ✓ I Saw a Success Message
                    </Button>
                    <Button fullWidth variant="secondary" onClick={() => setVerificationState('troubleshoot')} data-id="element-3201" data-mp="2upsywstscqrk">
                      ↻ Try Again
                    </Button>
                    <button onClick={() => setVerificationState('troubleshoot')} className={`w-full text-sm font-medium pt-1 ${isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-3202" data-mp="34mfjrhm43htl">
                      Having Trouble?
                    </button>
                  </div>
                </motion.div>}

              {verificationState === 'troubleshoot' && <motion.div key="troubleshoot" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="space-y-6 pt-4" data-id="element-3203" data-mp="r06hzu8k57la">
                  <div className="text-center mb-6" data-id="element-3204" data-mp="d9t8vojxjmud">
                    <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${isLight ? 'bg-red-50 text-red-600' : 'bg-red-500/10 text-red-400'}`} data-id="element-3205" data-mp="d91kaxor2pdl">
                      <AlertTriangle className="w-8 h-8" data-id="element-3206" data-mp="3lq67bk3wvmpg" />
                    </div>
                    <h2 className={`text-xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3207" data-mp="3lo1sw88dz5ax">
                      Deactivation Didn't Work
                    </h2>
                    <p className={`text-sm ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3208" data-mp="1tudd4zzenwms">
                      We couldn't confirm the deactivation. This may happen for
                      one of the following reasons:
                    </p>
                  </div>

                  <ul className={`text-sm space-y-3 p-5 rounded-2xl ${isLight ? 'bg-slate-50 text-slate-700' : 'bg-white/5 text-zinc-300'}`} data-id="element-3209" data-mp="1f4xllhs1vn61">
                    <li className="flex items-start gap-2" data-id="element-3210" data-mp="16513y042vokh">
                      <span className="text-vexu mt-0.5" data-id="element-3211" data-mp="166lznt24pdjx">•</span> The
                      deactivation was cancelled.
                    </li>
                    <li className="flex items-start gap-2" data-id="element-3212" data-mp="w6v5jshhd0kd">
                      <span className="text-vexu mt-0.5" data-id="element-3213" data-mp="w7d5az3cpwx1">•</span> Your carrier
                      requires a different deactivation code.
                    </li>
                    <li className="flex items-start gap-2" data-id="element-3214" data-mp="2p1cjgmkcfe94">
                      <span className="text-vexu mt-0.5" data-id="element-3215" data-mp="2b2l8h90s28p7">•</span> The request
                      wasn't completed.
                    </li>
                    <li className="flex items-start gap-2" data-id="element-3216" data-mp="2g0mtw0loek3u">
                      <span className="text-vexu mt-0.5" data-id="element-3217" data-mp="21ct9m2lnatex">•</span> Your mobile
                      plan doesn't support this feature.
                    </li>
                  </ul>

                  <div className="space-y-3 pt-4" data-id="element-3218" data-mp="265cpx7bpw2nf">
                    <Button fullWidth onClick={() => setVerificationState('idle')} data-id="element-3219" data-mp="1sd67tfp07llc">
                      Try Again
                    </Button>
                    <Button fullWidth variant="secondary" data-id="element-3220" data-mp="1if8kcueyxsts">
                      Contact Support
                    </Button>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>;
      case 3:
        // Screen 3 – All Rules Off
        return <motion.div key="step3" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="h-full flex flex-col p-6 overflow-y-auto pb-24" data-id="element-3221" data-mp="zrg8ig1vo1qx">
            <div className="mt-12 mb-8 text-center" data-id="element-3222" data-mp="1inpsfjzgnmiu">
              <div className={`w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center ${isLight ? 'bg-amber-50 text-amber-600' : 'bg-amber-500/10 text-amber-400'}`} data-id="element-3223" data-mp="1wfmizjlhdod4">
                <ShieldAlert className="w-10 h-10" data-id="element-3224" data-mp="26dk809b5zp6s" />
              </div>
              <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3225" data-mp="21ez685g9kxg8">
                Your AI is no longer answering your calls
              </h1>
              <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3226" data-mp="28yw4zgf0g3ei">
                All AI answering rules have been turned off. Your subscription
                is still active, but your AI assistant won't answer any calls
                until you enable at least one answering rule again.
              </p>
            </div>

            <div className="mt-auto space-y-4" data-id="element-3227" data-mp="1q84kpjthislc">
              <div className="space-y-1" data-id="element-3228" data-mp="1q9fuc09m2ukd">
                <p className={`text-xs font-bold uppercase tracking-wider text-center mb-2 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-3229" data-mp="23zj6fyes17w3">
                  Recommended
                </p>
                <Button fullWidth onClick={onKeepSubscription} data-id="element-3230" data-mp="1biglqp8s060s">
                  Keep My Subscription
                </Button>
                <p className={`text-xs text-center mt-2 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3231" data-mp="17gva177z9xs6">
                  I'll enable my AI again later.
                </p>
              </div>

              <div className="pt-4 space-y-3" data-id="element-3232" data-mp="1269ftmtnz5q3">
                <Button fullWidth variant="secondary" onClick={onEnableAnotherRule} data-id="element-3233" data-mp="ngldci9qy10s">
                  Enable an Answering Rule
                </Button>
                <button onClick={() => setStep(4)} className={`w-full py-3 text-sm font-medium transition-colors ${isLight ? 'text-red-500 hover:text-red-600' : 'text-red-400 hover:text-red-300'}`} data-id="element-3234" data-mp="1g4qxp3ww1o03">
                  Cancel Subscription
                </button>
              </div>
            </div>
          </motion.div>;
      case 4:
        // Screen 4 – Cancel Subscription Confirm
        return <motion.div key="step4" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="h-full flex flex-col p-6 items-center justify-center text-center" data-id="element-3235" data-mp="3vvlw2ln3jxbu">
            <h2 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3236" data-mp="1noidgmlrthas">
              Cancel Your Subscription?
            </h2>
            <p className={`text-sm mb-8 leading-relaxed max-w-xs ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3237" data-mp="1enaf64j98ggp">
              Cancelling your subscription will stop future renewals and disable
              your AI assistant until you subscribe again.
            </p>
            <div className="w-full space-y-3" data-id="element-3238" data-mp="341qdiajzkn1y">
              <Button fullWidth onClick={() => setStep(3)} data-id="element-3239" data-mp="341gnwo2ushws">
                Keep Subscription
              </Button>
              <Button fullWidth variant="secondary" onClick={() => setStep(5)} data-id="element-3240" data-mp="2z0sxa3ije333">
                <span className="text-red-500" data-id="element-3241" data-mp="36lvevghb0u29">Cancel Subscription</span>
              </Button>
            </div>
          </motion.div>;
      case 5:
        // Screen 5 – Subscription Cancelled
        return <motion.div key="step5" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="h-full flex flex-col items-center justify-center p-6 text-center" data-id="element-3242" data-mp="3geavoagv7clk">
            <div className={`w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center ${isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-3243" data-mp="2xlzxuv12i4ub">
              <CheckCircle2 className="w-8 h-8" data-id="element-3244" data-mp="cvrcdxg5zh2w" />
            </div>
            <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3245" data-mp="cxlnyl5jpwub">
              Subscription Cancelled
            </h1>
            <p className={`text-sm leading-relaxed mb-8 max-w-xs ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3246" data-mp="fgfkrp1lgs2g">
              Your subscription has been cancelled successfully. You can
              reactivate your AI assistant at any time by subscribing again.
            </p>
            <Button fullWidth onClick={onCancelSubscription} data-id="element-3247" data-mp="kdp9f2h5szp8">
              Return to Home
            </Button>
          </motion.div>;
      case 6:
        // Screen – Turn Off Wi-Fi (before carrier deactivation code)
        return <motion.div key="step6" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="h-full flex flex-col p-6" data-id="element-3248" data-mp="1854g17fyy36a">
            <button onClick={() => setStep(1)} className={`absolute top-6 left-6 p-2 rounded-full z-10 ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-400'}`} data-id="element-3249" data-mp="1d1l4k2r2yjjw">
              <ChevronLeft className="w-5 h-5" data-id="element-3250" data-mp="1mra5sfuq4y9q" />
            </button>

            <div className="mt-12 mb-8 text-center" data-id="element-3251" data-mp="1mr1uuti5w316">
              <h1 className={`text-2xl font-bold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3252" data-mp="1rplh6v0o3gu2">
                Almost There
              </h1>
              <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3253" data-mp="1hrynv3imfmqk">
                Before removing your carrier's call forwarding settings, we need
                to turn off Wi-Fi for a moment.
              </p>
            </div>

            <div className={`p-5 rounded-3xl mb-8 ${isLight ? 'bg-amber-50 border border-amber-200' : 'bg-amber-500/10 border border-amber-500/20'}`} data-id="element-3254" data-mp="u2w8k2bgm5z8">
              <div className="flex items-center gap-3 mb-3" data-id="element-3255" data-mp="z3jvabsno0nn">
                <div className="p-2 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400" data-id="element-3256" data-mp="fxvt7ypxp964">
                  <WifiOff className="w-5 h-5" data-id="element-3257" data-mp="b4t0ttww85ri" />
                </div>
                <h3 className={`font-bold ${isLight ? 'text-amber-900' : 'text-amber-400'}`} data-id="element-3258" data-mp="66hrdkaifx9t">
                  Temporarily Turn Off Wi-Fi
                </h3>
              </div>
              <p className={`text-sm leading-relaxed ${isLight ? 'text-amber-800' : 'text-amber-200/80'}`} data-id="element-3259" data-mp="yux05p31tjcp">
                Some mobile carriers require Wi-Fi to be turned off while
                updating call forwarding using carrier deactivation codes.
              </p>
              <p className={`text-sm leading-relaxed mt-3 font-medium ${isLight ? 'text-amber-900' : 'text-amber-400'}`} data-id="element-3260" data-mp="jxwars8by0he">
                You can turn Wi-Fi back on immediately after setup.
              </p>
            </div>

            <div className="mt-auto space-y-3" data-id="element-3261" data-mp="1k75q7nn2piaw">
              <Button fullWidth onClick={() => setStep(2)} data-id="element-3262" data-mp="1k8gudokfks2j">
                Wi-Fi is Off
              </Button>
              <button onClick={() => setStep(1)} className={`w-full py-3 text-sm font-medium ${isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-400 hover:text-zinc-200'}`} data-id="element-3263" data-mp="1p68kqe5fg64m">
                Back
              </button>
            </div>
          </motion.div>;
    }
  };
  return <div className={`h-full w-full relative overflow-hidden ${isLight ? 'bg-stone-100' : 'bg-black'}`} data-id="element-3264" data-mp="27w6tkwh1u39b">
      <AnimatePresence mode="wait" data-id="element-3265" data-mp="2cva0kmglajjx">{renderStep()}</AnimatePresence>
    </div>;
}